import React, { Fragment, useEffect, useState } from "react";
import { Intent } from "@blueprintjs/core";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { DateToLocal } from "components/dates";
import { Numeric } from "components/formatting";
import { LicenceCheckingService } from "services/LicenceCheckingService";

export function LicenceCheckingCreditExpiry() {
    const tableHeaders = [
        "Credit Type",
        "Purchase Date",
        "Credits Purchased",
        "Credits Refunded",
        "Credits Used",
        "Credit Expiry Date",
        "Credits Expired",
        "Credits Remaining"
    ];
    const noDataMessage = "There are no credits due to expire";
    const dateFormat = "DD MMM YYYY";
    const defaultPageSize = 25;

    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [tableRecordCount, setTableRecordCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    useEffect(() => {
        getCreditExpiry();
    }, []);

    function getCreditExpiry() {
        setLoading(true);

        const errorHandling = function () {
            NotificationToaster.show(
                Intent.DANGER,
                "Failed to get credit expiry. Please refresh the page."
            );
            setTableData([]);
            setTableRecordCount(0);
            setPageNumber(1);
        };

        LicenceCheckingService.getCreditExpiry()
            .then((response) => {
                // The API returns the rows already ordered by purchase date, so they are rendered in
                // the order received rather than sorted again here.
                const expiryData = response.map((credit) => {
                    const rowKey = `${credit.creditType}-${credit.purchaseDate}`;

                    return [
                        credit.creditType,
                        <DateToLocal key={`purchase-date-${rowKey}`} format={dateFormat}>{credit.purchaseDate}</DateToLocal>,
                        <Numeric key={`purchased-${rowKey}`}>{credit.purchasedCredits}</Numeric>,
                        <Numeric key={`refunded-${rowKey}`}>{credit.refundedCredits}</Numeric>,
                        <Numeric key={`used-${rowKey}`}>{credit.usedCredits}</Numeric>,
                        <DateToLocal key={`expiry-date-${rowKey}`} format={dateFormat}>{credit.expiryDate}</DateToLocal>,
                        <Numeric key={`expired-${rowKey}`}>{credit.expiredCredits}</Numeric>,
                        <Numeric key={`remaining-${rowKey}`}>{credit.remainingCredits}</Numeric>
                    ];
                });

                setTableData(expiryData);
                setTableRecordCount(expiryData.length);
                // Back to page one, so a shorter reload cannot strand the user past the last row.
                setPageNumber(1);
            }, errorHandling)
            .catch(errorHandling)
            .finally(() => {
                setLoading(false);
            });
    }

    function onPagingChange(page, perPage) {
        setPageNumber(page);
        setPageSize(perPage);
    }

    // ListingTable renders every row it is handed, so the page slices the data itself and passes
    // the full count for the page total.
    const startIndex = (pageNumber - 1) * pageSize;
    const pagedData = tableData.slice(startIndex, startIndex + pageSize);

    return (
        <Fragment>
            <h2>Credit Expiry</h2>

            <ListingTable
                id="listing-table-credit-expiry"
                noDataMessage={noDataMessage}
                headers={tableHeaders}
                data={pagedData}
                totalRecordCount={tableRecordCount}
                pageSize={pageSize}
                onPagingChange={onPagingChange}
                allowTableScroll={true}
                loadingData={loading}
            />
        </Fragment>
    );
}
